import { useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { ArrowRight, Camera, Mail, Smartphone } from 'lucide-react';
import { PhoneScanArt } from './PhoneScanArt';

/** Paige on Google Play: where the invite email's Get Paige button goes too. */
export const PAIGE_APP_LINK = 'https://play.google.com/store/apps/details?id=com.ademero.paige';

/**
 * The top of Scan from a phone: what it is, as a drawing and one line, then how, as three pictures
 * joined by arrows (the email sheet's steps): get the app from its QR code, sign in with their email,
 * scan, and the page appears in this job. While Paige Mobile is not in the app store (`listed` false), it says the
 * app is on its way and offers no store link or code: the link was a dead end (K-31, 2026-10-27).
 */
export function PhoneScanIntro({ listed }: { listed: boolean }): ReactNode {
  return (
    <section
      className="export-stage paige-phone-intro"
      aria-label="How scanning from a phone works"
    >
      <PhoneScanArt />
      <div className="paige-phone-intro-words">
        <p>
          {listed
            ? 'People out of the office snap paperwork with the Paige app. It lands in this job.'
            : 'Paige Mobile is on its way to the app store. Phone scanning starts here once it is there.'}
        </p>
        <ol className="paige-email-sheet-steps paige-phone-steps">
          <GetTheApp listed={listed} />
          <Arrow />
          <li>
            <span className="paige-email-sheet-step-icon" aria-hidden="true">
              <Mail size={22} />
            </span>
            <strong>Sign in with their email</strong>
            <small>Paige emails a code</small>
          </li>
          <Arrow />
          <li>
            <span className="paige-email-sheet-step-icon" aria-hidden="true">
              <Camera size={22} />
            </span>
            <strong>Scan</strong>
            <small>It appears here</small>
          </li>
        </ol>
      </div>
    </section>
  );
}

/** The first step: the store's code and link, or, while the app is not in the store, that it is on its way. */
function GetTheApp({ listed }: { listed: boolean }): ReactNode {
  return listed ? (
    <li>
      <AppCode />
      <strong>Get the app</strong>
      <small>
        <a href={PAIGE_APP_LINK} target="_blank" rel="noreferrer">
          Google Play
        </a>
      </small>
    </li>
  ) : (
    <li>
      <span className="paige-email-sheet-step-icon" aria-hidden="true">
        <Smartphone size={22} />
      </span>
      <strong>Get the app</strong>
      <small>On its way to the app store</small>
    </li>
  );
}

function Arrow(): ReactNode {
  return (
    <li className="paige-email-sheet-step-arrow" aria-hidden="true">
      <ArrowRight size={16} />
    </li>
  );
}

/** The app's QR code, drawn here from the store link; a blank square until it is drawn. */
function AppCode(): ReactNode {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(PAIGE_APP_LINK, { margin: 1, width: 128 })
      .then((url) => live && setQr(url))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return qr ? (
    <img
      className="paige-phone-qr"
      src={qr}
      width={56}
      height={56}
      alt="QR code for the Paige app on Google Play"
    />
  ) : (
    <span className="paige-phone-qr" aria-hidden="true" />
  );
}
